import { UserCatalogDto } from '@/api/generator';
import { api } from '@/api';
import { NotificationMessage } from '@/components/notifications/types';
import { AnyApiError, ApiError } from '@/errorHandling/typeGuards';
import { useSystemStore } from '@/stores/systemStore';
import { TFunction } from 'i18next';
import { type Ref, ref } from 'vue';

export function useExceptionDetails(
  t: TFunction,
  exceptionNotification: Ref<NotificationMessage | undefined> = ref(),
  loggedInUsers: Ref<UserCatalogDto[] | undefined> = ref(),
) {
  async function handleLoggedInUsersError(): Promise<boolean> {
    useSystemStore().fetchMaintenanceModeInfo();

    const { result, success } = await api.usersApi.getOtherUsersLoggedInCatalog();
    if (!success || !result || result.length === 0) return false;

    loggedInUsers.value = result;
    exceptionNotification.value = {
      type: 'error',
      message: t('Exceptions.ThereAreSomeLoggedInUsersMsg', { count: result.length }),
      closable: false,
    };
    return true;
  }

  async function handleExceptionDetails(error: AnyApiError) {
    // Maintenance mode can surface on any operation, so it is checked before the
    // internal-server narrowing below.
    if (error.name === ApiError.maintenanceMode) {
      useSystemStore().fetchMaintenanceModeInfo();
      return error;
    }

    // Only the logged-in-users case below is handled; other internal-server errors fall through.
    if (error.name !== ApiError.internalServer) {
      return error;
    }

    try {
      const isLoggedInUsersError =
        error.error?.exception?.i18n?.key === 'Exceptions.ThereAreSomeLoggedInUsersMsg';

      if (isLoggedInUsersError) {
        const handled = await handleLoggedInUsersError();
        // Suppressed because the dialog renders the notification and user list itself.
        if (handled) return undefined;
      }
    } catch (caughtError: unknown) {
      console.debug(caughtError);
    }

    return error;
  }

  function reset() {
    exceptionNotification.value = undefined;
    loggedInUsers.value = undefined;
  }

  return {
    handleExceptionDetails,
    reset,
    loggedInUsers,
  };
}
